import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";
const TM = () => <sup className="align-super text-[0.5em] text-white/45">™</sup>;

export default function About() {
  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="about-page">
      <Seo
        title="About Adaptience | Preparing for Life's Uncertainty"
        description="Adaptience is the learned human capability to prepare for, navigate, adapt to, recover from and grow through life's inevitable uncertainty — practised through interactive simulations."
        canonical="/about"
      />
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>
      <Navbar />
      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 py-16 sm:px-10 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>About</p>
          <h1 className="max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            Life doesn't always give us time to prepare.
          </h1>

          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/55">
            We prepare extensively for the situations we can see coming. Yet many of life's difficult moments arrive without rehearsal — uncertainty, pressure, rejection, emergencies, change and setbacks.
          </p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/55">
            Often, we discover how we respond only when the situation is already real.
          </p>

          <p className="mt-10 max-w-3xl text-xl leading-relaxed text-white/85 sm:text-2xl">
            Adaptience<TM /> is the learned human capability to prepare for, navigate, adapt to, recover from, and grow through life's inevitable uncertainty.
          </p>

          <div className="mt-16 border-t border-white/[0.07] pt-14">
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">What if we could practise before it matters?</h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
              Adaptience<TM /> lets people practise navigating difficult real-life situations before encountering them for real.
            </p>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/65">
              Through interactive simulations, people can make decisions, see how they respond, reflect on their choices, and discover what they could strengthen.
            </p>
            <p className="mt-8 max-w-2xl font-display text-xl font-medium leading-snug tracking-tight text-white/85 sm:text-2xl">
              Not to predict what will happen.
              <span className="block" style={{ color: ACCENT }}>But to be better prepared for how we navigate it when it does.</span>
            </p>
          </div>

          <div className="mt-16 border-t border-white/[0.07] pt-14">
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">Built for the situations life brings.</h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/60">
              Adaptience<TM /> spans six areas of real-life experience:
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-3 text-base text-white/70 sm:text-lg" data-testid="about-areas">
              {["Everyday Life Skills", "Social & Relationships", "Decisions & Pressure", "Safety & Emergencies", "Change & Uncertainty", "Setbacks & Adversity"].map((a, i, arr) => (
                <span key={a} className="flex items-center gap-3">
                  {a}
                  {i < arr.length - 1 && <span className="text-white/20">·</span>}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-20 border-l-2 pl-6" style={{ borderColor: ACCENT }}>
            <p className="font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">We can't predict every situation life will bring.</p>
            <p className="mt-1 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl" style={{ color: ACCENT }}>But we can practise how we navigate it.</p>
          </div>

          <Link
            to="/simulations"
            data-testid="about-explore-btn"
            className="group mt-16 inline-flex items-center gap-3 rounded-full border px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-[color,background-color] duration-500 active:scale-[0.98]"
            style={{ borderColor: ACCENT, color: ACCENT }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = ACCENT; }}
          >
            Explore Simulations
            <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <div className="mt-24">
          <SiteFooter />
        </div>
      </main>
    </div>
  );
}
